/**
 * API Client Service
 * Handles all HTTP requests to the backend API
 * Conditionally uses mocks in development mode
 */

import { getToken } from './auth';
import { Services, getIdentity } from './config/services';

// Runtime API base URL - MUST be set by consumer app
// Used when the library is pre-built and cannot read consumer's env vars
let _apiBaseUrl: string | null = null;

/**
 * Set runtime API base URL.
 *
 * REQUIRED: Must be called on app initialization before any API calls.
 *
 * Pre-built libraries cannot read the consumer app's environment variables.
 * The consumer must pass the URL explicitly at runtime.
 *
 * @param url - The base URL for API calls (e.g., "http://localhost:10000" or "/api/agent")
 *
 * @example
 * // In consumer app's initialization (e.g., RuntimeConfigProvider)
 * import { setApiBaseUrl } from "@eureka/ai-assistant";
 * setApiBaseUrl("/api/agent"); // or process.env.NEXT_PUBLIC_AGENT_API_URL
 *
 * @throws Error if url is empty or null
 */
export function setApiBaseUrl(url: string): void {
  if (!url || !url.trim()) {
    throw new Error(
      'API base URL is required. Call setApiBaseUrl(url) with a valid URL before using the library.'
    );
  }
  _apiBaseUrl = url.trim();
  console.log('[apiClient] API base URL set to:', _apiBaseUrl);
}

/**
 * Check if API base URL has been configured
 */
export function isApiBaseUrlSet(): boolean {
  return _apiBaseUrl !== null;
}

// Get API base URL - throws if not set
function getApiBaseUrl(): string {
  if (!_apiBaseUrl) {
    throw new Error(
      'API base URL not configured. Call setApiBaseUrl(url) before using the library. ' +
      'Example: setApiBaseUrl("/api/agent") or setApiBaseUrl("http://localhost:10000")'
    );
  }
  return _apiBaseUrl;
}

import type {
  StartAgentRequest,
  StartAgentResponse,
  SendInputRequest,
  SendInputResponse,
  GetMessagesResponse,
  ListSessionsResponse,
  ErrorResponse,
  ResolveAgentRequest,
  ResolveAgentResponse,
  ConversationCreate,
  ConversationUpdate,
  ConversationResponse,
  ConversationListResponse,
  ConversationWithExecutions,
  ConversationMessagesResponse,
  ConversationStatus,
  AgentCatalogResponse,
  PlatformConversationCreate,
  PlatformConversationResponse,
  InlineSkillConfiguration,
} from '../types';

// NOTE: API_BASE_URL is now a function call (getApiBaseUrl()) to ensure basePath is evaluated at request time

// Runtime WebSocket URL override
// Used when WebSocket needs a different URL than HTTP (e.g., in Kubernetes with Next.js proxy)
let _websocketBaseUrl: string | null = null;

/**
 * Set runtime WebSocket base URL.
 * Use this when WebSocket connections need to go directly to the agent service
 * instead of through an HTTP proxy (like Next.js API routes).
 *
 * @param url - The base URL for WebSocket connections (e.g., "ws://localhost:8000")
 *              Pass empty string or null to use default behavior (same as HTTP)
 */
export function setWebSocketBaseUrl(url: string | null): void {
  _websocketBaseUrl = url && url.trim() ? url : null;
  if (_websocketBaseUrl) {
    console.log('[apiClient] WebSocket base URL set to:', _websocketBaseUrl);
  }
}

// Runtime MCP Manager URL - MUST be set by consumer app
// Used when MCP servers are on a separate service from agents
let _mcpBaseUrl: string | null = null;

/**
 * Set runtime MCP Manager base URL.
 *
 * REQUIRED: Must be called on app initialization before any MCP calls.
 *
 * @param url - The base URL for MCP Manager (e.g., "http://localhost:10010" or "/api/mcp")
 *
 * @example
 * // In consumer app's initialization (e.g., RuntimeConfigProvider)
 * import { setMcpBaseUrl } from "@eureka/ai-assistant";
 * setMcpBaseUrl("/api/mcp"); // or process.env.MCP_MANAGER_URL
 *
 * @throws Error if url is empty or null
 */
export function setMcpBaseUrl(url: string): void {
  if (!url || !url.trim()) {
    throw new Error(
      'MCP base URL is required. Call setMcpBaseUrl(url) with a valid URL before using the library.'
    );
  }
  _mcpBaseUrl = url.trim();
  console.log('[apiClient] MCP base URL set to:', _mcpBaseUrl);
}

/**
 * Check if MCP base URL has been configured
 */
export function isMcpBaseUrlSet(): boolean {
  return _mcpBaseUrl !== null;
}

// Get MCP base URL - throws if not set
export function getMcpBaseUrl(): string {
  if (!_mcpBaseUrl) {
    throw new Error(
      'MCP base URL not configured. Call setMcpBaseUrl(url) before using the library. ' +
      'Example: setMcpBaseUrl("/api/mcp") or setMcpBaseUrl("http://localhost:10010")'
    );
  }
  return _mcpBaseUrl;
}

// Get auth headers for API requests
async function getAuthHeaders(): Promise<Record<string, string>> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    // CSRF protection: Required by server-side proxy to validate same-origin requests
    'x-requested-with': 'XMLHttpRequest',
  };

  const token = await getToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  // Add tenant header - use getIdentity().tenantId
  // Send all header variants for compatibility with different backends
  const identity = getIdentity();
  if (identity.tenantId) {
    headers['tenant'] = identity.tenantId;
    headers['x-tenant-id'] = identity.tenantId;
  }

  // Add user-id header for conversation filtering
  if (identity.userId) {
    headers['user-id'] = identity.userId;
    headers['x-user-id'] = identity.userId;
  }

  return headers;
}

// Helper to handle API responses
async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const errorData = await response.json().catch(() => null);

    // Handle different error formats
    let errorMessage: string;
    if (errorData?.detail) {
      // FastAPI/Pydantic format: { detail: "..." }
      errorMessage = typeof errorData.detail === 'string'
        ? errorData.detail
        : JSON.stringify(errorData.detail);
    } else if (errorData?.error?.message) {
      // Custom format: { error: { message: "..." } }
      errorMessage = errorData.error.message;
    } else {
      // Fallback
      errorMessage = `HTTP ${response.status}: ${response.statusText}`;
    }

    throw new Error(errorMessage);
  }
  return response.json();
}

// Helper to check if URL is relative (starts with /)
function isRelativeUrl(url: string): boolean {
  return url.startsWith('/') && !url.startsWith('//');
}

// Helper to get origin for relative URLs
function getOrigin(): string {
  if (typeof window !== 'undefined') {
    return window.location.origin;
  }
  // Server-side fallback
  return 'http://localhost:3000';
}

// Helper to build URL with query params
function buildUrl(path: string, params?: Record<string, string | number>): string {
  // Properly join base URL and path (handles paths starting with /)
  const apiBaseUrl = getApiBaseUrl();
  const base = apiBaseUrl.endsWith('/') ? apiBaseUrl.slice(0, -1) : apiBaseUrl;
  const pathNormalized = path.startsWith('/') ? path : `/${path}`;
  const fullUrl = `${base}${pathNormalized}`;

  // Handle relative URLs by using window.location.origin as base
  const urlBase = isRelativeUrl(fullUrl) ? getOrigin() : undefined;
  const url = urlBase ? new URL(fullUrl, urlBase) : new URL(fullUrl);

  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      url.searchParams.append(key, String(value));
    });
  }
  return url.toString();
}

/**
 * Resolve agent type from user input
 *
 * This function intelligently matches user input to available agents using LLM.
 * Handles short names, natural language, and ambiguous requests.
 *
 * @param agentType - User input (e.g., "simple", "sync database", "help me analyze data")
 * @returns Resolution result with confidence scores and alternatives
 *
 * @example
 * // Direct match
 * const result = await resolveAgentType("simple");
 * // result.status === "matched"
 * // result.resolved_agent_type === "test/simple"
 * // result.confidence === 1.0
 *
 * @example
 * // Natural language match
 * const result = await resolveAgentType("sync database tables");
 * // result.status === "matched" or "needs_clarification"
 * // result.resolved_agent_type === "data_sync/data_sync_coordinator"
 * // result.alternatives contains other options
 */
async function resolveAgentType(agentType: string): Promise<ResolveAgentResponse> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${getApiBaseUrl()}/agent/resolve`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ agent_type: agentType }),
  });

  return handleResponse<ResolveAgentResponse>(response);
}

/**
 * Generate a simple UUID v4
 */
function generateUUID(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

/**
 * Start a new agent session
 */
async function startAgent(request: StartAgentRequest): Promise<StartAgentResponse> {
  // Add required fields with defaults from centralized config
  // Use nullish coalescing (??) to only fallback on null/undefined, not empty string
  // This ensures tenant_id passed from caller takes precedence
  const { configuration, ...restRequest } = request;

  const requestWithDefaults: Record<string, unknown> = {
    ...restRequest,
    user_id: request.user_id ?? getIdentity().userId,
    tenant_id: request.tenant_id ?? getIdentity().tenantId,
    project_id: request.project_id ?? getIdentity().projectId,
  };

  // Build configuration object - pass through as-is
  // File-based agents read bindings from runtime_binding.yaml (not from API request)
  if (configuration) {
    requestWithDefaults.configuration = configuration;
  }

  // Debug: log the request
  const inputObj = requestWithDefaults.input as { prompt?: string } | undefined;
  console.log('🚀 startAgent request:', {
    prompt: inputObj?.prompt?.substring(0, 50) + '...',
    agent_id: requestWithDefaults.agent_id,
    input_resources: requestWithDefaults.input_resources,
    has_resources: !!(requestWithDefaults.input_resources as string[])?.length,
    configuration: requestWithDefaults.configuration,
  });

  const headers = await getAuthHeaders();
  const response = await fetch(`${getApiBaseUrl()}/agent/start`, {
    method: 'POST',
    headers,
    body: JSON.stringify(requestWithDefaults),
  });

  return handleResponse<StartAgentResponse>(response);
}

// =============================================================================
// RESOURCE UPLOAD API
// =============================================================================

/**
 * Resource upload response from POST /resources
 */
export interface ResourceUploadResponse {
  resource_id: string;
  filename: string;
  content: string;
  size_bytes: number;
  checksum: string;
  created_at: string;
  resource_type: string;
}

/**
 * Read file content as text or base64 depending on file type
 */
async function readFileContent(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    // Determine if this is a text-based file
    const isTextFile = file.type.startsWith('text/') ||
      file.name.endsWith('.json') ||
      file.name.endsWith('.csv') ||
      file.name.endsWith('.md') ||
      file.name.endsWith('.yaml') ||
      file.name.endsWith('.yml') ||
      file.name.endsWith('.xml') ||
      file.name.endsWith('.txt');

    reader.onload = () => {
      if (isTextFile) {
        // Text files: return as-is
        resolve(reader.result as string);
      } else {
        // Binary files: extract base64 from data URL
        const dataUrl = reader.result as string;
        const base64 = dataUrl.split(',')[1] || '';
        resolve(base64);
      }
    };
    reader.onerror = () => reject(new Error('Failed to read file'));

    if (isTextFile) {
      reader.readAsText(file);
    } else {
      reader.readAsDataURL(file);
    }
  });
}

/**
 * Upload a file to the resources service
 * Returns resource_id for use in agent requests
 *
 * Backend uses content-addressable storage (deduplication)
 */
async function uploadResource(
  file: File,
  params: {
    tenant_id: string;
    resource_type?: 'prompt' | 'config' | 'test' | 'example' | 'doc' | 'agent_input';
    metadata?: Record<string, unknown>;
  }
): Promise<ResourceUploadResponse> {
  // Read file content
  const content = await readFileContent(file);

  const requestBody = {
    tenant_id: params.tenant_id,
    filename: file.name,
    resource_type: params.resource_type || 'agent_input',
    content: content,
    metadata: params.metadata,
  };

  console.log('📤 Upload resource request:', {
    url: `${getApiBaseUrl()}/resources`,
    tenant_id: params.tenant_id,
    filename: file.name,
    resource_type: requestBody.resource_type,
    content_length: content.length,
  });

  const headers = await getAuthHeaders();
  const response = await fetch(`${getApiBaseUrl()}/resources`, {
    method: 'POST',
    headers,
    body: JSON.stringify(requestBody),
  });

  return handleResponse<ResourceUploadResponse>(response);
}

/**
 * Send user input to an existing agent session
 */
async function sendInput(
  agentId: string,
  request: SendInputRequest
): Promise<SendInputResponse> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${getApiBaseUrl()}/agent/${agentId}/input`, {
    method: 'POST',
    headers,
    body: JSON.stringify(request),
  });

  return handleResponse<SendInputResponse>(response);
}

/**
 * Get message history for an agent session
 */
async function getMessages(
  agentId: string,
  limit?: number,
  beforeMessageId?: string
): Promise<GetMessagesResponse> {
  const params: Record<string, string | number> = {};
  if (limit) params.limit = limit;
  if (beforeMessageId) params.before_message_id = beforeMessageId;

  const url = buildUrl(`/agent/${agentId}/messages`, params);
  const headers = await getAuthHeaders();
  const response = await fetch(url, {
    method: 'GET',
    headers,
  });

  return handleResponse<GetMessagesResponse>(response);
}

/**
 * List all agent sessions
 */
async function listSessions(
  limit?: number,
  offset?: number
): Promise<ListSessionsResponse> {
  const params: Record<string, string | number> = {};
  if (limit) params.limit = limit;
  if (offset) params.offset = offset;

  const url = buildUrl('/agent/sessions', params);
  const headers = await getAuthHeaders();
  const response = await fetch(url, {
    method: 'GET',
    headers,
  });

  return handleResponse<ListSessionsResponse>(response);
}

/**
 * Get WebSocket URL for agent session
 * Accepts the websocket_url path returned by backend (e.g., "/agent/exec-123/stream")
 * Handles both absolute URLs (http://localhost:10000) and relative URLs (/api/agent)
 *
 * If a WebSocket base URL override is set (via setWebSocketBaseUrl), it uses that instead.
 * This is needed when HTTP requests go through a proxy but WebSocket needs direct connection.
 */
function getWebSocketUrl(websocketPath: string): string {
  // Remove leading slash if present since we'll add it in the URL construction
  const path = websocketPath.startsWith('/') ? websocketPath.slice(1) : websocketPath;

  // Use WebSocket override if set (for cases where WS needs direct connection)
  if (_websocketBaseUrl) {
    // Parse the override URL to get protocol and host
    const wsBase = _websocketBaseUrl.replace(/\/$/, ''); // Remove trailing slash
    // If override is already ws:// or wss://, use directly
    if (wsBase.startsWith('ws://') || wsBase.startsWith('wss://')) {
      return `${wsBase}/${path}`;
    }
    // If override is http:// or https://, convert to ws:// or wss://
    if (wsBase.startsWith('http://') || wsBase.startsWith('https://')) {
      const wsProtocol = wsBase.startsWith('https') ? 'wss' : 'ws';
      const baseUrl = wsBase.replace(/^https?:\/\//, '');
      return `${wsProtocol}://${baseUrl}/${path}`;
    }
    // If override is a relative path (starts with /), use window.location
    if (wsBase.startsWith('/') && typeof window !== 'undefined') {
      const wsProtocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
      return `${wsProtocol}://${window.location.host}${wsBase}/${path}`;
    }
    // Assume it's a host:port, use ws://
    return `ws://${wsBase}/${path}`;
  }

  // Get API base URL at request time
  const apiBaseUrl = getApiBaseUrl();

  // Handle relative apiBaseUrl (e.g., /api/agent)
  if (isRelativeUrl(apiBaseUrl)) {
    // Use window.location to build WebSocket URL
    if (typeof window !== 'undefined') {
      const wsProtocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
      const basePath = apiBaseUrl.endsWith('/') ? apiBaseUrl.slice(0, -1) : apiBaseUrl;
      return `${wsProtocol}://${window.location.host}${basePath}/${path}`;
    }
    // Server-side fallback
    return `ws://localhost:3000${apiBaseUrl}/${path}`;
  }

  // Handle absolute URL (e.g., http://localhost:10000)
  const wsProtocol = apiBaseUrl.startsWith('https') ? 'wss' : 'ws';
  const baseUrl = apiBaseUrl.replace(/^https?:\/\//, '');
  return `${wsProtocol}://${baseUrl}/${path}`;
}

/**
 * Get agent catalog - list of available agents
 * Optionally filter by category (mode)
 */
async function getAgentCatalog(params: {
  tenant_id: string;
  user_id?: string;
  category?: string;
}): Promise<AgentCatalogResponse> {
  const queryParams: Record<string, string> = {
    tenant_id: params.tenant_id,
  };
  if (params.user_id) {
    queryParams.user_id = params.user_id;
  }
  if (params.category) {
    queryParams.category = params.category;
  }

  const url = buildUrl('/agent/catalog/foundational', queryParams);
  const headers = await getAuthHeaders();
  const response = await fetch(url, {
    method: 'GET',
    headers,
  });

  return handleResponse<AgentCatalogResponse>(response);
}

// =============================================================================
// CONVERSATION API (Chat-Based Agent Lifecycle)
// =============================================================================

/**
 * Create a new conversation (exploration session)
 */
async function createConversation(data: ConversationCreate): Promise<ConversationResponse> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${getApiBaseUrl()}/lifecycle/conversations`, {
    method: 'POST',
    headers,
    body: JSON.stringify(data),
  });

  return handleResponse<ConversationResponse>(response);
}

/**
 * Create a platform conversation with inline agent definition.
 * Used when agent definition is provided at runtime (not pre-registered).
 *
 * The agent is auto-registered on first use:
 * - Same name + same version = reuse existing
 * - Same name + new version = create new version
 * - New name = create new agent
 *
 * @param data - Platform conversation creation request with inline agent definition
 * @returns Platform conversation response with conversation_id and execution_id
 */
async function createPlatformConversation(
  data: PlatformConversationCreate
): Promise<PlatformConversationResponse> {
  const headers = await getAuthHeaders();

  // Add identity defaults from centralized config
  const requestData: PlatformConversationCreate = {
    ...data,
    tenant_id: data.tenant_id ?? getIdentity().tenantId,
    project_id: data.project_id ?? getIdentity().projectId,
    user_id: data.user_id ?? getIdentity().userId,
  };

  console.log('🚀 createPlatformConversation:', {
    agent_name: data.agent_definition.name,
    agent_version: data.agent_definition.version,
    goal: data.goal?.substring(0, 50) + (data.goal && data.goal.length > 50 ? '...' : ''),
  });

  const response = await fetch(
    `${getApiBaseUrl()}/lifecycle/platform/conversations`,
    {
      method: 'POST',
      headers,
      body: JSON.stringify(requestData),
    }
  );

  return handleResponse<PlatformConversationResponse>(response);
}

/**
 * Get a conversation by ID
 */
async function getConversation(
  conversationId: string,
  tenantId: string
): Promise<ConversationResponse> {
  const url = buildUrl(`/lifecycle/conversations/${conversationId}`, { tenant_id: tenantId });
  const headers = await getAuthHeaders();
  const response = await fetch(url, {
    method: 'GET',
    headers,
  });

  return handleResponse<ConversationResponse>(response);
}

/**
 * Get a conversation with its executions
 */
async function getConversationWithExecutions(
  conversationId: string,
  tenantId: string
): Promise<ConversationWithExecutions> {
  const url = buildUrl(`/lifecycle/conversations/${conversationId}/executions`, { tenant_id: tenantId });
  const headers = await getAuthHeaders();
  const response = await fetch(url, {
    method: 'GET',
    headers,
  });

  return handleResponse<ConversationWithExecutions>(response);
}

/**
 * List conversations with filtering and pagination
 */
async function listConversations(params: {
  tenant_id: string;
  project_id?: string;
  user_id?: string;
  status?: ConversationStatus;
  mode?: string;  // Filter by lifecycle mode (explore, codify, etc.)
  agent_type?: string;  // Filter by agent type (e.g., "data_operations/data_discovery")
  page?: number;
  page_size?: number;
}): Promise<ConversationListResponse> {
  const queryParams: Record<string, string | number> = {
    tenant_id: params.tenant_id,
  };
  if (params.project_id) queryParams.project_id = params.project_id;
  if (params.user_id) queryParams.user_id = params.user_id;
  if (params.status) queryParams.status = params.status;
  if (params.mode) queryParams.mode = params.mode;
  if (params.agent_type) queryParams.agent_type = params.agent_type;
  if (params.page) queryParams.page = params.page;
  if (params.page_size) queryParams.page_size = params.page_size;

  const url = buildUrl('/lifecycle/conversations', queryParams);
  const headers = await getAuthHeaders();
  const response = await fetch(url, {
    method: 'GET',
    headers,
  });

  return handleResponse<ConversationListResponse>(response);
}

/**
 * Update a conversation
 */
async function updateConversation(
  conversationId: string,
  tenantId: string,
  data: ConversationUpdate
): Promise<ConversationResponse> {
  const url = buildUrl(`/lifecycle/conversations/${conversationId}`, { tenant_id: tenantId });
  const headers = await getAuthHeaders();
  const response = await fetch(url, {
    method: 'PATCH',
    headers,
    body: JSON.stringify(data),
  });

  return handleResponse<ConversationResponse>(response);
}

/**
 * Delete (archive) a conversation
 */
async function deleteConversation(
  conversationId: string,
  tenantId: string
): Promise<void> {
  const url = buildUrl(`/lifecycle/conversations/${conversationId}`, { tenant_id: tenantId });
  const headers = await getAuthHeaders();
  const response = await fetch(url, {
    method: 'DELETE',
    headers,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    throw new Error(errorData?.detail || `HTTP ${response.status}: ${response.statusText}`);
  }
}

/**
 * Get conversation messages for chat UI restoration
 * Returns messages from conversation_messages table
 */
async function getConversationMessages(
  conversationId: string,
  tenantId: string,
  page: number = 1,
  pageSize: number = 100
): Promise<ConversationMessagesResponse> {
  const url = buildUrl(`/lifecycle/conversations/${conversationId}/messages`, {
    tenant_id: tenantId,
    page,
    page_size: pageSize,
  });
  const headers = await getAuthHeaders();
  const response = await fetch(url, {
    method: 'GET',
    headers,
  });

  return handleResponse<ConversationMessagesResponse>(response);
}

// =============================================================================
// RESOURCE API (File Content Viewing)
// =============================================================================

/**
 * Resource content response from GET /resources/{resource_id}
 */
export interface ResourceResponse {
  resource_id: string;
  filename: string;
  content: string;
  content_type: string;
  size?: number;
  created_at?: string;
  metadata?: Record<string, unknown>;
}

/**
 * Get resource content by ID
 * Used for viewing files linked in agent output (e.g., [file.md](/resources/uuid))
 */
async function getResource(resourceId: string): Promise<ResourceResponse> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${getApiBaseUrl()}/resources/${resourceId}`, {
    method: 'GET',
    headers,
  });

  return handleResponse<ResourceResponse>(response);
}

/**
 * Get raw resource content (for download)
 * Returns the file with appropriate content-type header
 */
function getResourceRawUrl(resourceId: string): string {
  return `${getApiBaseUrl()}/resources/${resourceId}/raw`;
}

// =============================================================================
// AGENT STOP API
// =============================================================================

/**
 * Stop agent response
 */
export interface StopAgentResponse {
  status: 'stop_requested';
  root_execution_id: string;
  requested_at: string;
}

/**
 * Stop a running agent execution
 * Only root executions can be stopped - stopping a root will stop all subagents
 */
async function stopAgent(rootExecutionId: string): Promise<StopAgentResponse> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${getApiBaseUrl()}/agent/${rootExecutionId}/stop`, {
    method: 'POST',
    headers,
  });

  return handleResponse<StopAgentResponse>(response);
}

/**
 * API Client object
 */
export const apiClient = {
  // Agent operations
  resolveAgentType,
  startAgent,
  stopAgent,
  sendInput,
  getMessages,
  listSessions,
  getWebSocketUrl,
  getAgentCatalog,

  // Conversation operations (lifecycle)
  createConversation,
  createPlatformConversation,
  getConversation,
  getConversationWithExecutions,
  getConversationMessages,
  listConversations,
  updateConversation,
  deleteConversation,

  // Resource operations
  uploadResource,
  getResource,
  getResourceRawUrl,

  // Runtime configuration
  setApiBaseUrl,
  setWebSocketBaseUrl,
};
